import {FC, useEffect, useState} from 'react';
import {ReactComponent as ImportIcon} from '@icons/import.svg';
import {ReactComponent as SearchIcon} from '@icons/search-normal.svg';
import {BODY1, defaultPage, defaultPageSize, SUBTITLE1} from '@/utils/constants';
import {Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from '@mui/material';
import ActionsMenu from '@/shared/components/Table/ActionMenu';
import Pagination from '@/shared/components/Pagination';
import Input from '@/shared/components/Inputs/Input';
import {ReactComponent as TestImage} from '@/assets/images/testImage.svg';
import Button from '@/shared/components/Button';
import {ReactComponent as ArrowUpDownIcon} from '@icons/arrow-up-down.svg';
import {ReportsModalTablesStyle} from './ReportsModalTables.style';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';
import {OrganizationReportsService} from '@/utils/services/organization-reports.service';
import {downloadFile, getErrorMessage} from '@/utils/helpers';
import {REPORTS_GET_FILE} from '@/utils/enums/organization-reports.enum';
import {toast} from 'react-toastify';
import {getReportLoadingSelector, getReportUnpaidItems, getUnpaidItemsSelector} from '@/store/organization-report';
import {IUnpaidItemsData} from '@/utils/types/organization-reports.interface';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import Loader from '@/shared/components/Loader';
import Modal from '@/shared/components/Modal';
import AttendeeDetails from '@/components/Organization/Attendees/AttendeeDetails';
import {getAttendee, getAttendeeSelector} from '@/store/organization-attendees';
import {attendeesTableStyle} from '@/components/Organization/Attendees/AttendeesTable/AttendeesTable.styles';
import AuctionDetails from '@/components/Organization/Auction/AuctionTable/AuctionDetails';
import {IOrganizationAuctionResponse} from '@/utils/types';
import {getAuction} from '@/store/organization-auction';
import {OrganizationAuctionService} from '@/utils/services';
import {getDashboardStatistics} from '@/store/organization-dashboard';
import {orgAPI} from '@/core/api';
import {OrganizationStripeService} from '@/utils/services/organization-stripe.services';
import {getTeamSelector} from '@/store/organization';
import {ITeamRole} from '@/utils/enums';
import {tablesAndTicketsTableStyle} from '@/components/Organization/TablesAndTickets/TablesAndTicketsTable/TablesAndTicketsTable.styles';

const columnOptions = ['id', 'item', 'winner', 'price', 'status'];

const UnpaidItemsModal: FC = () => {
  const dispatch = useAppDispatch();
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const unpaidItemsList = useAppSelector(getUnpaidItemsSelector);
  const isLoading = useAppSelector(getReportLoadingSelector);
  const attendee = useAppSelector(getAttendeeSelector);
  const team = useAppSelector(getTeamSelector);

  const isStandartMember = !!team && team.role === ITeamRole.STANDART;

  const [searchName, setSearchName] = useState('');
  const [page, setPage] = useState<number>(defaultPage);
  const [filterValues, setFilterValues] = useState(unpaidItemsList?.data || []);
  const [sortState, setSortState] = useState<{column: keyof IUnpaidItemsData | null; direction: 'asc' | 'desc'}>({
    column: null,
    direction: 'asc',
  });
  const [filteredAllValues, setFilteredAllValues] = useState<IUnpaidItemsData[]>([]);
  const [isShowAttendeeDetailsModal, setIsShowAttendeeDetailsModal] = useState<boolean>(false);
  const [isShowAuctionItemDetailsModal, setIsShowAuctionItemDetailsModal] = useState<boolean>(false);
  const [isProfilePage, setIsProfilePage] = useState<boolean>(false);
  const [editableAuction, setEditableAuction] = useState<IOrganizationAuctionResponse | null>(null);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState<boolean>(false);

  const handleAccessModalChange = () => setIsAccessModalOpen(!isAccessModalOpen);

  const handleProfileTabStyles = (isProfilePage: boolean) => setIsProfilePage(isProfilePage);

  const handleChangeAttendeeDetailsModal = async (attendeeId?: number) => {
    setIsShowAttendeeDetailsModal(!isShowAttendeeDetailsModal);

    if (!isShowAttendeeDetailsModal && attendeeId) {
      try {
        await dispatch(getAttendee(attendeeId)).unwrap();
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }

    if (isShowAttendeeDetailsModal) {
      getUnpaidItemsData();
    }
  };

  const handleChangeAuctionDetailsModal = async (id?: number) => {
    setIsShowAuctionItemDetailsModal(!isShowAuctionItemDetailsModal);
    setEditableAuction(null);

    if (!isShowAuctionItemDetailsModal && id) {
      try {
        const data = await dispatch(getAuction({id, apiInstance: orgAPI})).unwrap();

        setEditableAuction(data);
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    }

    if (isShowAuctionItemDetailsModal) {
      getUnpaidItemsData();
    }
  };

  const getUnpaidItemsData = async () => {
    if (!currentEvent) return;

    try {
      const reqBody = {
        eventId: currentEvent?.id,
        page,
        searchName,
      };

      const {data} = await dispatch(getReportUnpaidItems(reqBody)).unwrap();

      setFilteredAllValues(data);
      setFilterValues(data.slice(0, defaultPageSize));
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const sortData = (column: keyof IUnpaidItemsData) => {
    const direction = sortState.column === column && sortState.direction === 'asc' ? 'desc' : 'asc';

    const sorted = [...filterValues].sort((a, b) => {
      const valA = a[column] as string | number;
      const valB = b[column] as string | number;

      if (typeof valA === 'number' && typeof valB === 'number') {
        return direction === 'asc' ? valA - valB : valB - valA;
      }

      return direction === 'asc' ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
    });

    setSortState({column, direction});
    setFilterValues(sorted);
  };

  const handleSearchChange = (name: string) => {
    setSearchName(name);
    setPage(1);
  };

  const handlePaginationChange = (step: number) => {
    setPage(step);

    const startIdx = (step - 1) * defaultPageSize;
    const endIdx = startIdx + defaultPageSize;
    setFilterValues(filteredAllValues.slice(startIdx, endIdx) || []);
  };

  const handleDownloadCSV = async () => {
    if (!currentEvent) return;

    try {
      const file = await OrganizationReportsService.getUnpaidItemsCsv(currentEvent?.id);
      downloadFile(file.data, REPORTS_GET_FILE.UNPAID_ITEMS);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUpdatePaymentStatus = async (id: number, isPaid: boolean) => {
    if (!currentEvent) return;
    if (isStandartMember) {
      handleAccessModalChange();
      return;
    }

    try {
      await OrganizationAuctionService.updateAuctionPaymentStatus(id, isPaid);
      toast.success('Payment status updated successfully');
      getUnpaidItemsData();
      await dispatch(getDashboardStatistics(currentEvent?.id)).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSendPaymentLink = async (id: number) => {
    try {
      await OrganizationStripeService.sendPaymentLink(id, currentEvent?.id as number);
      toast.success('Payment link sent successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const paginationText = unpaidItemsList?.totalCount
    ? `Showing ${(page - 1) * defaultPageSize + 1} - ${Math.min(
        page * defaultPageSize,
        unpaidItemsList?.totalCount ?? 0
      )} of ${unpaidItemsList?.totalCount} items`
    : 'Showing 0 items';

  useEffect(() => {
    if (!unpaidItemsList?.data) return;

    setFilteredAllValues(unpaidItemsList?.data);
    setFilterValues(unpaidItemsList?.data.slice(0, defaultPageSize));
  }, []);

  useEffect(() => {
    getUnpaidItemsData();
  }, [currentEvent, page, searchName]);

  return (
    <Box sx={ReportsModalTablesStyle.wrapper}>
      <Box sx={ReportsModalTablesStyle.filterRow}>
        <Typography variant={SUBTITLE1} sx={ReportsModalTablesStyle.title}>
          {paginationText}
        </Typography>

        <Box sx={ReportsModalTablesStyle.search}>
          <SearchIcon />
          <Input placeholder="Search" value={searchName} onChange={handleSearchChange} />
        </Box>
        <Box>
          <Button
            sx={ReportsModalTablesStyle.download}
            startIcon={<ImportIcon />}
            onClick={handleDownloadCSV}
            title="Download.csv"
          />
        </Box>
      </Box>
      {isLoading ? (
        <Loader parentClassName={ReportsModalTablesStyle.loader} size={50} />
      ) : filterValues.length > 0 ? (
        <Box sx={ReportsModalTablesStyle.table}>
          <TableContainer>
            <Table sx={ReportsModalTablesStyle.tableStyle} aria-label="simple table">
              <TableHead>
                <TableRow sx={{height: '32px'}}>
                  {columnOptions.map((col) => {
                    const sortable = col === 'price' || col === 'id';

                    return (
                      <TableCell
                        className={`${col}`}
                        key={col}
                        onClick={() => sortable && sortData(col)}
                        sx={{
                          cursor: sortable ? 'pointer' : 'default',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                          {col.charAt(0).toUpperCase() + col.slice(1)}
                          <Box sx={{width: '20px', display: 'flex', alignItems: 'center'}}>
                            {sortable ? <ArrowUpDownIcon /> : null}
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {filterValues.map((row: IUnpaidItemsData, index) => (
                  <TableRow key={index} sx={{padding: '11px 0px'}}>
                    {columnOptions.map((col) => {
                      if (col === 'winner') {
                        return (
                          <TableCell key={col} className="winner">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'left',
                                flexDirection: 'column',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography sx={{fontSize: '14px', fontWeight: '700 !important'}}>
                                {row.winnerName}
                              </Typography>
                              <Typography sx={{color: 'var(--neutral-300) !important'}}>{row.winnerEmail}</Typography>
                            </Box>
                          </TableCell>
                        );
                      }

                      if (col === 'item') {
                        return (
                          <TableCell key={col} className="item">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                py: '8px',
                                gap: '16px',
                              }}
                            >
                              {row.photo ? (
                                <img src={row.photo} alt="item" width="48px" height="48px" />
                              ) : (
                                <TestImage />
                              )}
                              <Box>
                                <Typography sx={{fontSize: '14px', fontWeight: '700 !important'}}>
                                  {row.name}
                                </Typography>
                                <Typography sx={{color: 'var(--neutral-300) !important'}}>{row.category}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                        );
                      }

                      if (col === 'status') {
                        return (
                          <TableCell key={col} className={col}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <Typography className={(row as Record<string, any>)[col]}>
                                {(row as Record<string, any>)[col]}
                              </Typography>
                              <Box
                                sx={{width: '5px', height: '5px', borderRadius: '50%'}}
                                className={`circle-${(row as Record<string, any>)[col]}`}
                              />
                            </Box>
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={col} className={col}>
                          <Typography
                            sx={{
                              color:
                                typeof (row as Record<string, any>)[col] === 'number' && col === 'price'
                                  ? 'var(--error-700) !important'
                                  : 'var(--neutral-500) !important',
                            }}
                          >
                            {typeof (row as Record<string, any>)[col] === 'number' && col === 'price'
                              ? `-$${(row as Record<string, any>)[col]}`
                              : (row as Record<string, any>)[col] || '-'}
                          </Typography>
                        </TableCell>
                      );
                    })}
                    <TableCell>
                      <ActionsMenu
                        content={
                          <Box sx={ReportsModalTablesStyle.menu}>
                            <Typography
                              sx={ReportsModalTablesStyle.menuContent}
                              onClick={() => {
                                //handleChangeAttendeeDetailsModal(row.winnerId);
                              }}
                              variant={BODY1}
                            >
                              Go to Profile
                            </Typography>
                            <Typography
                              sx={ReportsModalTablesStyle.menuContent}
                              onClick={() => {
                                handleChangeAuctionDetailsModal(row.id);
                              }}
                              variant={BODY1}
                            >
                              Go to Item Details
                            </Typography>
                            <Typography
                              sx={ReportsModalTablesStyle.menuContent}
                              onClick={() => handleSendPaymentLink(row.winnerId)}
                              variant={BODY1}
                            >
                              Send Payment Link
                            </Typography>
                            <Typography
                              sx={ReportsModalTablesStyle.menuContent}
                              onClick={() => {
                                handleUpdatePaymentStatus(row.id, true);
                              }}
                              variant={BODY1}
                            >
                              Mark as Paid
                            </Typography>
                          </Box>
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={ReportsModalTablesStyle.paginationContainer}>
            <Pagination
              page={page}
              pageSize={defaultPageSize}
              total={unpaidItemsList?.totalCount || 0}
              onChange={(step) => handlePaginationChange(step)}
            />
          </Box>
        </Box>
      ) : (
        <EmptyTableData />
      )}

      <Modal
        onClose={handleChangeAttendeeDetailsModal}
        open={isShowAttendeeDetailsModal && !!attendee}
        sx={{...attendeesTableStyle.detailsModal, ...(isProfilePage && attendeesTableStyle.profilePage)}}
      >
        <AttendeeDetails
          onCloseModal={handleChangeAttendeeDetailsModal}
          handleProfileTabStyles={handleProfileTabStyles}
        />
      </Modal>
      <Modal
        onClose={handleChangeAuctionDetailsModal}
        open={isShowAuctionItemDetailsModal}
        sx={attendeesTableStyle.detailsModal}
      >
        <AuctionDetails onCloseModal={handleChangeAuctionDetailsModal} editableAuction={editableAuction} />
      </Modal>
      <Modal
        open={isAccessModalOpen}
        onClose={handleAccessModalChange}
        title="Access Denied"
        description="You do not have access to this feature"
        sx={tablesAndTicketsTableStyle.modalNotify}
      />
    </Box>
  );
};

export default UnpaidItemsModal;
